/**
 * Ende-zu-Ende-Verschluesselung fuer den "gesicherten Modus" (Zero-Knowledge).
 *
 * Alle Inhalte gesicherter Module (Wissen, Investitionen, ...) werden
 * ausschliesslich im Browser mit AES-256-GCM ver- und entschluesselt (WebCrypto
 * API). Der zufaellige Master-Key wird nie im Klartext gespeichert oder gesendet:
 * er wird mit einem aus Passphrase (+ optionaler Schluesseldatei) abgeleiteten
 * Schluessel "verpackt". Der Server sieht nur Salze und verpackte/verschluesselte
 * Werte.
 */

const PBKDF2_ITERATIONEN = 310000;
const SALT_LAENGE = 16;
const IV_LAENGE = 12;
const MASTER_LAENGE = 32;

const KEYFILE_MARKER = 'TRESOR-KEYFILE-v1';
// Alte Schluesseldateien trugen den Wissen-Marker; beide werden akzeptiert.
const KEYFILE_MARKER_PREFIXE = ['TRESOR-KEYFILE', 'WISSEN-KEYFILE'];

const encoder = new TextEncoder();
const decoder = new TextDecoder();

// ── Kodierung ─────────────────────────────────────────────
const zuBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binaer = '';
  bytes.forEach((b) => (binaer += String.fromCharCode(b)));
  return btoa(binaer);
};

const vonBase64 = (base64) => {
  const binaer = atob(base64);
  const bytes = new Uint8Array(binaer.length);
  for (let i = 0; i < binaer.length; i += 1) bytes[i] = binaer.charCodeAt(i);
  return bytes;
};

const zufallsBytes = (laenge) => crypto.getRandomValues(new Uint8Array(laenge));

// ── Schlusselableitung ────────────────────────────────────

/** Leitet aus Passphrase (+ optionaler Schluesseldatei) und Salt einen AES-GCM-Schluessel ab. */
async function leiteVerpackungsSchluesselAb(passphrase, keyfileBytes, saltBytes) {
  const basis = await crypto.subtle.importKey('raw', encoder.encode(passphrase), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: saltBytes, iterations: PBKDF2_ITERATIONEN, hash: 'SHA-256' },
    basis,
    256
  );
  let schluesselBytes = new Uint8Array(bits);
  if (keyfileBytes) {
    // Zweiter Faktor: Passphrase-Ableitung mit der Schluesseldatei zu einem Wert verschmelzen.
    const kombiniert = new Uint8Array(schluesselBytes.length + keyfileBytes.length);
    kombiniert.set(schluesselBytes, 0);
    kombiniert.set(keyfileBytes, schluesselBytes.length);
    schluesselBytes = new Uint8Array(await crypto.subtle.digest('SHA-256', kombiniert));
  }
  return crypto.subtle.importKey('raw', schluesselBytes, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

const importiereMasterKey = (bytes) =>
  crypto.subtle.importKey('raw', bytes, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);

// ── Roh-Ver-/Entschluesselung ─────────────────────────────
async function aesVerschluessle(schluessel, ivBytes, datenBytes) {
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: ivBytes }, schluessel, datenBytes);
  return new Uint8Array(ct);
}

async function aesEntschluessle(schluessel, ivBytes, ctBytes) {
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: ivBytes }, schluessel, ctBytes);
  return new Uint8Array(pt);
}

// ── Wiederherstellungs-Code ───────────────────────────────

/** Erzeugt einen 128-Bit-Wiederherstellungs-Code als Gruppen (z.B. "A1B2-C3D4-..."). */
function erzeugeWiederherstellungsCode() {
  const hex = Array.from(zufallsBytes(16))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
  return hex.match(/.{1,4}/g).join('-');
}

// ── Oeffentliche API ──────────────────────────────────────

/**
 * Erzeugt einen frischen Tresor: Master-Key, Verpackung per Passphrase und per
 * Wiederherstellungs-Code, optional eine Schluesseldatei.
 * @returns {Promise<{vault: object, masterKey: CryptoKey, recoveryCode: string, keyfileBase64: string|null}>}
 */
export async function erstelleTresor(passphrase, mitKeyfile) {
  const masterBytes = zufallsBytes(MASTER_LAENGE);
  const keyfileBytes = mitKeyfile ? zufallsBytes(32) : null;

  const salt = zufallsBytes(SALT_LAENGE);
  const wrapIv = zufallsBytes(IV_LAENGE);
  const verpackung = await leiteVerpackungsSchluesselAb(passphrase, keyfileBytes, salt);
  const wrappedKey = await aesVerschluessle(verpackung, wrapIv, masterBytes);

  const recoveryCode = erzeugeWiederherstellungsCode();
  const recoverySalt = zufallsBytes(SALT_LAENGE);
  const recoveryIv = zufallsBytes(IV_LAENGE);
  const recoverySchluessel = await leiteVerpackungsSchluesselAb(recoveryCode, null, recoverySalt);
  const recoveryWrapped = await aesVerschluessle(recoverySchluessel, recoveryIv, masterBytes);

  return {
    vault: {
      salt: zuBase64(salt),
      wrappedKey: zuBase64(wrappedKey),
      wrapIv: zuBase64(wrapIv),
      recoverySalt: zuBase64(recoverySalt),
      recoveryWrappedKey: zuBase64(recoveryWrapped),
      recoveryIv: zuBase64(recoveryIv),
      keyfileRequired: Boolean(keyfileBytes),
    },
    masterKey: await importiereMasterKey(masterBytes),
    recoveryCode,
    keyfileBase64: keyfileBytes ? zuBase64(keyfileBytes) : null,
  };
}

/** Entsperrt den Tresor mit Passphrase (+ Schluesseldatei). Wirft bei falschen Angaben. */
export async function entsperreTresor(vault, passphrase, keyfileBytes) {
  const verpackung = await leiteVerpackungsSchluesselAb(passphrase, keyfileBytes, vonBase64(vault.salt));
  const masterBytes = await aesEntschluessle(verpackung, vonBase64(vault.wrapIv), vonBase64(vault.wrappedKey));
  return importiereMasterKey(masterBytes);
}

/** Entsperrt ueber den Wiederherstellungs-Code; liefert Master-Key und Roh-Bytes zum Neu-Verpacken. */
export async function entsperreMitCode(vault, recoveryCode) {
  const schluessel = await leiteVerpackungsSchluesselAb(recoveryCode.trim().toUpperCase(), null, vonBase64(vault.recoverySalt));
  const masterBytes = await aesEntschluessle(schluessel, vonBase64(vault.recoveryIv), vonBase64(vault.recoveryWrappedKey));
  return { masterKey: await importiereMasterKey(masterBytes), masterBytes };
}

/**
 * Verpackt einen bereits entsperrten Master-Key mit einer neuen Passphrase neu
 * (Wiederherstellungs-Daten bleiben erhalten). Fuer Passphrase-Aenderung.
 */
export async function verpackeNeu(masterBytes, passphrase, mitKeyfile, altesVault) {
  const keyfileBytes = mitKeyfile ? zufallsBytes(32) : null;
  const salt = zufallsBytes(SALT_LAENGE);
  const wrapIv = zufallsBytes(IV_LAENGE);
  const verpackung = await leiteVerpackungsSchluesselAb(passphrase, keyfileBytes, salt);
  const wrappedKey = await aesVerschluessle(verpackung, wrapIv, masterBytes);
  return {
    vault: {
      salt: zuBase64(salt),
      wrappedKey: zuBase64(wrappedKey),
      wrapIv: zuBase64(wrapIv),
      recoverySalt: altesVault.recoverySalt,
      recoveryWrappedKey: altesVault.recoveryWrappedKey,
      recoveryIv: altesVault.recoveryIv,
      keyfileRequired: Boolean(keyfileBytes),
    },
    keyfileBase64: keyfileBytes ? zuBase64(keyfileBytes) : null,
  };
}

/** Verschluesselt ein JSON-Objekt; liefert Ciphertext und IV (Base64). */
export async function verschluessleJson(masterKey, objekt) {
  const iv = zufallsBytes(IV_LAENGE);
  const ct = await aesVerschluessle(masterKey, iv, encoder.encode(JSON.stringify(objekt)));
  return { cipherText: zuBase64(ct), iv: zuBase64(iv) };
}

/** Entschluesselt ein zuvor mit {@link verschluessleJson} erzeugtes Objekt. */
export async function entschluessleJson(masterKey, cipherText, iv) {
  const pt = await aesEntschluessle(masterKey, vonBase64(iv), vonBase64(cipherText));
  return JSON.parse(decoder.decode(pt));
}

/** Verschluesselt Roh-Bytes (Datei-Inhalt); liefert Ciphertext (Base64) und IV. */
export async function verschluessleBytes(masterKey, arrayBuffer) {
  const iv = zufallsBytes(IV_LAENGE);
  const ct = await aesVerschluessle(masterKey, iv, new Uint8Array(arrayBuffer));
  return { datenBase64: zuBase64(ct), datenIv: zuBase64(iv) };
}

/** Entschluesselt Datei-Bytes zurueck in einen ArrayBuffer. */
export async function entschluessleBytes(masterKey, datenBase64, datenIv) {
  const pt = await aesEntschluessle(masterKey, vonBase64(datenIv), vonBase64(datenBase64));
  return pt.buffer;
}

/** Liest eine Schluesseldatei (Text mit Base64-Zeile) in Roh-Bytes. */
export function leseKeyfile(text) {
  const istMarker = (zeile) => KEYFILE_MARKER_PREFIXE.some((p) => zeile.startsWith(p));
  const zeile = text
    .split(/\r?\n/)
    .map((z) => z.trim())
    .filter((z) => z && !istMarker(z))
    .pop();
  if (!zeile) throw new Error('Ungueltige Schluesseldatei');
  return vonBase64(zeile);
}

/** Baut den Textinhalt einer herunterladbaren Schluesseldatei. */
export const baueKeyfileInhalt = (keyfileBase64) => `${KEYFILE_MARKER}\n${keyfileBase64}\n`;
